import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { LOGO_URL, ORG } from "../lib/constants";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/testimonials", label: "Stories" },
  { to: "/apply", label: "Apply" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-brand-cream/90 backdrop-blur-md border-b border-brand-line shadow-sm"
          : "bg-brand-cream/60 backdrop-blur-sm"
      }`}
    >
      <div className="container-editorial flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Hosea Ministries Foundation" className="h-11 w-11 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif text-[17px] text-brand-charcoal tracking-tight">{ORG.name}</div>
            <div className="font-sans text-[11px] tracking-[0.18em] uppercase text-brand-rose">
              Faith · Recovery · Restoration
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-sans text-sm tracking-wide link-underline ${
                  isActive ? "text-brand-teal font-semibold" : "text-brand-charcoal hover:text-brand-teal"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={ORG.donateUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-donate-btn"
            className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-brand-tealDark transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
          >
            <Heart className="w-4 h-4" /> Donate
          </a>
        </nav>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-full hover:bg-brand-bone transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-brand-charcoal" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-brand-cream border-brand-line w-[85%] max-w-sm">
            <div className="flex items-center justify-between pb-6 border-b border-brand-line">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
                <span className="font-serif text-brand-charcoal">Hosea Ministries</span>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 mt-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-serif text-2xl py-3 border-b border-brand-line/60 ${
                      isActive ? "text-brand-teal" : "text-brand-charcoal"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={ORG.donateUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-nav-donate"
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium"
              >
                <Heart className="w-4 h-4" /> Donate
              </a>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
